import { type MouseEvent, useState } from 'react';
import { getActionAriaLabel, getActionLabel } from '@/shared/copy/actions';
import { r, s } from '@/theme/tokens';
import { Text } from '@/ui';
import { Box, IconButton, Menu, MenuItem, Tooltip } from '@/ui/primitives';
import {
  DeleteFilledIcon as DeleteIcon,
  EditOutlinedIcon,
  LocalOfferIcon,
  MoreVertIcon,
  RestoreFromTrashOutlinedIcon,
} from '@/ui/primitives/icons';
import { TableRowTagsDialog } from './TableRowTagsDialog';
import type { TableActionsCellProps } from './TableCells.types';

const tableActionsMenuItemSx = {
  minHeight: '40px',
  gap: s('md'),
  px: s('lg'),
  py: s('md'),
};

export const TableActionsCell = ({
  rowName,
  verticalAlign = 'top',
  disabledTooltip = null,
  editActionLabel = null,
  deleteActionLabel = getActionLabel('delete'),
  editTagsEntityNoun = 'test',
  rowTags = [],
  rowTagColors = [],
  showEditTagsAction = true,
  onEdit,
  disableEdit = false,
  onUpdateTagsColor,
  updateTagsColorLoading = false,
  onDelete,
  disableDelete = false,
  onRestore,
  disableRestore = false,
  restoreActionLabel = getActionLabel('restore'),
}: TableActionsCellProps): JSX.Element => {
  const resolvedRowName = rowName.trim() || 'this test';
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [editTagsOpen, setEditTagsOpen] = useState(false);
  const menuOpen = Boolean(anchorEl);
  const actionsDisabled = disabledTooltip !== null;

  const handleOpen = (event: MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: verticalAlign === 'center' ? 'center' : 'flex-start',
        pt: verticalAlign === 'center' ? '0' : '8px',
      }}
    >
      <Tooltip
        title={disabledTooltip ?? ''}
        disableHoverListener={!actionsDisabled}
        placement="top"
      >
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <IconButton
            aria-label={getActionAriaLabel('rowActions', rowName)}
            aria-haspopup={actionsDisabled ? undefined : 'menu'}
            aria-expanded={!actionsDisabled && menuOpen ? 'true' : undefined}
            size="medium"
            onClick={actionsDisabled ? undefined : handleOpen}
            disabled={actionsDisabled}
            sx={{
              width: '28px',
              height: '28px',
              minWidth: '28px',
              minHeight: '28px',
              p: '0',
              borderRadius: r('pill'),
              color: actionsDisabled ? 'text.disabled' : undefined,
              '&:hover, &.Mui-focusVisible': actionsDisabled
                ? undefined
                : {
                    backgroundColor: 'rgba(255, 255, 255, 0.22)',
                  },
            }}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={!actionsDisabled && menuOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: s('sm'),
              border: (theme) => `1px solid ${theme.app.colors.border}`,
              borderRadius: `${r('card')} !important`,
              backgroundColor: 'background.paper',
              backgroundImage: 'none',
              boxShadow: 'none',
              '--Paper-overlay': 'none',
            },
          },
          list: {
            sx: {
              px: s('none'),
              py: s('none'),
            },
          },
        }}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {onEdit && editActionLabel ? (
          <MenuItem
            disabled={disableEdit}
            sx={tableActionsMenuItemSx}
            onClick={(event) => {
              event.stopPropagation();
              handleClose();
              onEdit();
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
            <Text role="body">{editActionLabel}</Text>
          </MenuItem>
        ) : null}
        {!onEdit && showEditTagsAction ? (
          <MenuItem
            disabled={updateTagsColorLoading}
            sx={tableActionsMenuItemSx}
            onClick={(event) => {
              event.stopPropagation();
              handleClose();
              setEditTagsOpen(true);
            }}
          >
            <LocalOfferIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
            <Text role="body">{editActionLabel ?? getActionLabel('editTags')}</Text>
          </MenuItem>
        ) : null}
        {onRestore ? (
          <MenuItem
            disabled={disableRestore}
            sx={tableActionsMenuItemSx}
            onClick={(event) => {
              event.stopPropagation();
              handleClose();
              onRestore();
            }}
          >
            <RestoreFromTrashOutlinedIcon
              sx={{ fontSize: (theme) => theme.app.iconSizes.sm, color: 'success.main' }}
            />
            <Text role="body">{restoreActionLabel}</Text>
          </MenuItem>
        ) : onDelete ? (
          <MenuItem
            disabled={disableDelete}
            sx={tableActionsMenuItemSx}
            onClick={(event) => {
              event.stopPropagation();
              handleClose();
              onDelete();
            }}
          >
            <DeleteIcon
              htmlColor="currentColor"
              sx={{ fontSize: (theme) => theme.app.iconSizes.sm, color: 'error.main' }}
            />
            <Text role="body">{deleteActionLabel}</Text>
          </MenuItem>
        ) : null}
      </Menu>
      <TableRowTagsDialog
        open={editTagsOpen}
        loading={updateTagsColorLoading}
        onClose={() => {
          if (updateTagsColorLoading) return;
          setEditTagsOpen(false);
        }}
        onConfirm={onUpdateTagsColor}
        editTagsEntityNoun={editTagsEntityNoun}
        rowName={resolvedRowName}
        rowTagColors={rowTagColors}
        rowTags={rowTags}
      />
    </Box>
  );
};

export { TableCellContainer } from './TableSharedCells';
